import clsx from 'clsx';

import { Button } from '@appkit4/react-components/button';
import { Avatar } from '@appkit4/react-components/avatar';
import { DropdownButton, ValueType } from '@appkit4/react-components/dropdown';
import { Modal } from '@appkit4/react-components/modal';
import { Tooltip } from '@appkit4/react-components/tooltip';

import { generateTimestamp } from '@utils/generateTimestamp';
import { getEmailInitials, getFullNameFromEmail } from '@utils/users';

import SessionCardThumbnail from '@assets/images/session-card-thumbnail.svg';
import styles from './SessionCard.module.scss';
import { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import { Input } from '@appkit4/react-components';
import ShareModal from '../ShareModal/ShareModal';
import RemoveUserModal from '../RemoveUserModal/RemoveUserModal';
import DuplicateModal from '../DuplicateModal/DuplicateModal';
import { ShareUser } from '@contexts/SessionProvider';
import Success from '@assets/images/Success.svg';
import { deleteSession, updateUserSession, removeShare, duplicateSession } from '@config/getSessionApis';
import { generateThumbnail } from '@config/getSessionApis';

import DesignSystemBadge from '../DesignSystemBadge';

interface iSessionCard {
    userEmail: string;
    ownerEmail?: string;
    sessionId: string;
    sessionName: string;
    lastEdited: number;
    favorite: string;
    thumbnail: string;
    generateImage: boolean;
    designSystem: string;
    isSharedSession?: boolean;
    isGenerateComplete?: boolean;
    onToggleFavorite?: () => void;
    onClickSession?: () => void;
    onUpdateSessionName?: (name: string) => void;
    onDeleteSession?: (sessionId: string) => void;
    onDuplicateSession?: () => void;
}

const SessionCard = forwardRef(
    (
        {
            userEmail,
            ownerEmail,
            sessionId,
            sessionName,
            lastEdited,
            favorite,
            designSystem,
            thumbnail,
            isSharedSession, // eslint-disable-line @typescript-eslint/no-unused-vars
            isGenerateComplete = true,
            onToggleFavorite,
            onClickSession,
            onUpdateSessionName,
            onDeleteSession,
            onDuplicateSession,
            generateImage,
            ...props
        }: iSessionCard,
        ref: any
    ) => {
        const isReadOnly = !!ownerEmail && ownerEmail !== userEmail;
        const isGenerating = !isGenerateComplete;

        const [visible, setVisible] = useState(false);
        const [isEditing, setIsEditing] = useState(false);
        const [topic, setTopic] = useState(sessionName);
        const [shareModalVisible, setShareModalVisible] = useState(false);
        const [duplicateModalVisible, setDuplicateModalVisible] = useState(false);
        const [removeUserModalVisible, setRemoveUserModalVisible] = useState(false);
        const [deleteSuccessVisible, setDeleteSuccessVisible] = useState(false);
        const [userRemove, setUserRemove] = useState({} as ShareUser);
        const [base64Thumbnail, setBase64Thumbnail] = useState(thumbnail);
        useImperativeHandle(ref, () => ({
            setIsEditing,
        }));

        useEffect(() => {
            if (!thumbnail && generateImage) {
                generateThumbnail(sessionName).then((res) => {
                    const base64Img = `data:image/jpeg;base64,${res.res.data[0].b64_json}`;
                    setBase64Thumbnail(base64Img);
                    updateUserSession(sessionId, userEmail, base64Img, {});
                });
            }
        }, [thumbnail]); // eslint-disable-line react-hooks/exhaustive-deps
        // Note: generateImage, sessionId, sessionName, and userEmail are stable values from props/context

        const handleContextSelect = async (value: ValueType, _: any, event: any) => {
            event.stopPropagation();

            if (isGenerating && (value === 'rename' || value === 'duplicate')) {
                return;
            }

            if (value === 'rename') {
                setIsEditing(true);
            } else if (value === 'duplicate') {
                handleDuplicate();
            } else if (value === 'delete') {
                setVisible(true);
            } else {
                setShareModalVisible(true);
            }
        };

        const handleDuplicate = async () => {
            try {
                setDuplicateModalVisible(true);
                await duplicateSession(sessionId);
                setDuplicateModalVisible(false);

                if (onDuplicateSession) {
                    onDuplicateSession();
                }
            } catch (error) {
                console.error('Error duplicating session:', error);
                setDuplicateModalVisible(false);
            }
        };

        const handleInputSessionName = (event: any) => {
            event?.stopPropagation();
        };

        const onChangeSessionName = (value: string) => {
            setTopic(value);
        };

        const onBlurSessionName = (event: any) => {
            if (event.target.value.trim() === '') {
                setTopic(sessionName);
                setIsEditing(false);
                return;
            }

            if (onUpdateSessionName) {
                onUpdateSessionName(topic.trim());
            }
        };

        const handleDeleteSession = async () => {
            if (isReadOnly) {
                try {
                    const shareRemove = await removeShare(userEmail, sessionId);
                    if (shareRemove.code === 1) {
                        setVisible(false);
                        setDeleteSuccessVisible(true);
                        if (onDeleteSession) {
                            onDeleteSession(sessionId);
                        }
                    }
                } catch {
                    console.log('Error removing user from session');
                }
            } else {
                try {
                    const deleteResponse = await deleteSession(userEmail, sessionId);
                    if (deleteResponse.code === 1) {
                        setVisible(false);
                        setDeleteSuccessVisible(true);
                        if (onDeleteSession) {
                            onDeleteSession(sessionId);
                        }
                    }
                } catch {
                    console.error('Error deleting session');
                }
            }
        };

        const handleClickSession = (event: any) => {
            event?.stopPropagation();
            if (onClickSession) {
                onClickSession();
            }
        };

        const handleToggleFavorite = (event: any) => {
            event.stopPropagation();
            if (onToggleFavorite) {
                onToggleFavorite();
            }
        };

        const renderContextItem = (label: React.ReactNode, item: any) => {
            return (
                <div className={styles['context-item']}>
                    <span className={`Appkit4-icon ${item.iconName}`} />
                    <span>{label}</span>
                </div>
            );
        };

        const handleShareModalCancel = () => {
            setShareModalVisible(false);
        };

        const handleDuplicateModalCancel = () => {
            setDuplicateModalVisible(false);
        };

        const handleRemoveUserModalCancel = () => {
            setRemoveUserModalVisible(false);
            setUserRemove({} as ShareUser);
            setShareModalVisible(true);
        };

        const handleRemoveUserConfirm = (user: any) => {
            setRemoveUserModalVisible(false);
            setUserRemove(user);
            setShareModalVisible(true);
        };

        const handleCloseModal = () => {
            setVisible(false);
        };

        const confirmModal = visible && (
            <Modal
                visible={visible}
                title={'Delete project'}
                ariaLabel={'Delete project'}
                onCancel={() => setVisible(false)}
                modalStyle={{ width: '33.75rem' }}
                footerStyle={{ paddingTop: '8px', marginTop: '-8px', minHeight: '64px' }}
                header={''}
                icons={''}
                footer={
                    <>
                        <Button onClick={() => handleCloseModal} kind="secondary">
                            Cancel
                        </Button>
                        <Button onClick={handleDeleteSession} kind="negative">
                            Delete
                        </Button>
                    </>
                }
                bodyStyle={{ minHeight: '60px' }}>
                <div className={styles['confirm-modal-body']}>
                    Are you sure you want to delete this project? This action is permanent.
                </div>
            </Modal>
        );

        const deleteSuccessModal = deleteSuccessVisible && (
            <Modal
                visible={deleteSuccessVisible}
                title={''}
                ariaLabel={'Delete project successfully'}
                onCancel={() => setDeleteSuccessVisible(false)}
                modalStyle={{ width: '500px' }}
                footerStyle={{ paddingTop: '8px', marginTop: '-8px', minHeight: '64px' }}
                className="delete-success-modal"
                header={''}
                icons={''}
                footer={
                    <>
                        <Button style={{ width: '100%' }} onClick={() => setDeleteSuccessVisible(false)} kind="primary">
                            Close
                        </Button>
                    </>
                }
                bodyStyle={{ minHeight: '60px' }}>
                <div className={styles['confirm-modal-body']}>
                    <Success />
                    <div className="ap-typography-heading-m ap-mt-spacing-6">Success!!</div>
                    <div className="ap-typography-body ap-mt-spacing-4">
                        Your project has been deleted successfully.
                    </div>
                </div>
            </Modal>
        );

        return (
            <>
                <div className={styles['session-card']} {...props} onClick={handleClickSession}>
                    <div
                        className={styles['image-wrapper']}
                        style={{ width: '231px', height: '113px', overflow: 'hidden' }}>
                        {base64Thumbnail ? (
                            <img
                                className={styles['thumbnail']}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                }}
                                src={base64Thumbnail}
                                alt="Session Thumbnail"
                            />
                        ) : (
                            <SessionCardThumbnail />
                        )}
                    </div>
                    {isEditing ? (
                        <Input
                            type={'text'}
                            hideTitleOnInput={true}
                            inputProps={{
                                autoFocus: true,
                            }}
                            onClick={handleInputSessionName}
                            value={topic}
                            onChange={onChangeSessionName}
                            onBlur={onBlurSessionName}
                        />
                    ) : (
                        <div className={clsx(styles['name-wrapper'], 'session-card-name-wrapper')}>
                            <span className={styles['session-name']}>{sessionName}</span>
                            <DropdownButton
                                className={styles['context-button']}
                                kind="text"
                                onSelect={handleContextSelect}
                                itemTemplate={renderContextItem}
                                ariaLabel="Context button"
                                data={[
                                    {
                                        label: 'Share',
                                        value: 'share',
                                        iconName: 'icon-share-outline',
                                        disabled: isReadOnly,
                                    },
                                    {
                                        label: 'Duplicate',
                                        value: 'duplicate',
                                        iconName: 'icon-duplicate-outline',
                                        disabled: isGenerating,
                                    },
                                    {
                                        label: 'Rename',
                                        value: 'rename',
                                        iconName: 'icon-edit-outline',
                                        disabled: isReadOnly || isGenerating,
                                    },
                                    {
                                        label: 'Delete',
                                        value: 'delete',
                                        iconName: 'icon-delete-outline',
                                    },
                                ]}>
                                <span className="Appkit4-icon icon-circle-more-outline" />
                            </DropdownButton>
                        </div>
                    )}
                    {isReadOnly ? (
                        <>
                            <div className={styles['details-wrapper']}>
                                <span className="session-time-stamp">{generateTimestamp(lastEdited)}</span>
                            </div>
                            <div className={styles['details-wrapper']}>
                                <Tooltip
                                    trigger="hover"
                                    position="top"
                                    distance={4}
                                    appendAfterTarget={false}
                                    content={getFullNameFromEmail(ownerEmail || '')}>
                                    <Avatar
                                        label={getEmailInitials(ownerEmail || '')}
                                        role="button"
                                        disabled={false}
                                        style={{ width: '24px', height: '24px', fontSize: '10.5px', fontWeight: '300' }}
                                    />
                                </Tooltip>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {(designSystem === 'Appkit' ||
                                        designSystem === 'AppkitRebrand' ||
                                        designSystem === 'Fluent' ||
                                        designSystem === 'MaterialUI') && (
                                        <DesignSystemBadge designSystem={designSystem} size="md" />
                                    )}
                                    <Button
                                        kind="text"
                                        onClick={handleToggleFavorite}
                                        className={styles['favorite-button']}
                                        aria-label="Favorite button">
                                        <span
                                            className={clsx(
                                                'Appkit4-icon',
                                                favorite ? 'icon-star-fill' : 'icon-star-outline'
                                            )}></span>
                                    </Button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className={styles['details-wrapper']}>
                                <span className="session-time-stamp">{generateTimestamp(lastEdited)}</span>
                            </div>
                            <div className={styles['details-wrapper']}>
                                {(designSystem === 'Appkit' ||
                                    designSystem === 'AppkitRebrand' ||
                                    designSystem === 'Fluent' ||
                                    designSystem === 'MaterialUI' ||
                                    designSystem === 'NewEra') && (
                                    <DesignSystemBadge designSystem={designSystem} size="md" />
                                )}
                                <Button
                                    kind="text"
                                    onClick={handleToggleFavorite}
                                    className={styles['favorite-button']}
                                    aria-label="Favorite button">
                                    <span
                                        className={clsx(
                                            'Appkit4-icon',
                                            favorite ? 'icon-star-fill' : 'icon-star-outline'
                                        )}></span>
                                </Button>
                            </div>
                        </>
                    )}
                </div>
                {confirmModal}
                {deleteSuccessVisible && deleteSuccessModal}
                {shareModalVisible && (
                    <ShareModal
                        visible={shareModalVisible}
                        title="Share project"
                        onCancel={handleShareModalCancel}
                        modalStyle={{ width: '500px' }}
                        activeSessionId={sessionId}></ShareModal>
                )}
                {removeUserModalVisible && (
                    <RemoveUserModal
                        visible={removeUserModalVisible}
                        title="Remove user?"
                        onCancel={handleRemoveUserModalCancel}
                        onRemoveConfirm={(user: any) => handleRemoveUserConfirm(user)}
                        modalStyle={{ width: '540px' }}
                        userRemove={userRemove}></RemoveUserModal>
                )}
                {duplicateModalVisible && (
                    <DuplicateModal
                        visible={duplicateModalVisible}
                        title="Duplicating file"
                        onCancel={handleDuplicateModalCancel}
                        modalStyle={{ width: '440px' }}></DuplicateModal>
                )}
            </>
        );
    }
);

export { SessionCard };
